import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { has_access } from "../../../middleware/privileges";
import { GET_RECIPES, GET_UNLINKED_PRODUCTS } from "./queries";
import { Recipe } from "./models";
import InventoryNav from "./inventoryNav";

const btn = {
  txt: lang("Nueva Receta", "New Recipe"),
  icon: "fa fa-plus",
  link: "/inventory/recipes/new",
  dataTour: "new-recipe-btn",
};

const RecipeList: React.FC = () => {
  const history = useHistory();
  const [search, setSearch] = useState("");

  const [fetchRecipes, { data, loading }] = useLazyQuery(GET_RECIPES, {
    fetchPolicy: "no-cache",
  });
  const [fetchUnlinked, { data: unlinkedData }] = useLazyQuery(
    GET_UNLINKED_PRODUCTS,
    { fetchPolicy: "no-cache" }
  );

  useEffect(() => {
    fetchRecipes({ variables: { take: 200 } });
    fetchUnlinked();
    //eslint-disable-next-line
  }, []);

  const recipes: Recipe[] = data?.getRecipes || [];
  const unlinkedCount = unlinkedData?.getUnlinkedProducts?.length || 0;

  const filtered = search
    ? recipes.filter((r) => {
        const term = search.toLowerCase();
        const productName = r.product?.title?.toLowerCase() || "";
        const recipeName = r.name?.toLowerCase() || "";
        return productName.includes(term) || recipeName.includes(term);
      })
    : recipes;

  const getMarginClass = (recipe: Recipe): string => {
    if (!recipe.food_cost || !recipe.product?.price) return "";
    const marginPct =
      ((recipe.product.price - recipe.food_cost) / recipe.product.price) * 100;
    if (marginPct < 30) return "text-danger font-weight-bold";
    if (marginPct < 50) return "text-warning";
    return "text-success";
  };

  return (
    <>
      <HeaderTitle
        title={lang("Recetas", "Recipes")}
        btn={has_access(256) ? btn : null}
      />
      <InventoryNav active="recipes" />

      {unlinkedCount > 0 && (
        <div className="alert alert-warning d-flex align-items-center mb-3" data-tour="unlinked-warning">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          <span>
            <strong>{unlinkedCount}</strong>{" "}
            {lang(
              "productos sin receta asignada",
              "products without assigned recipe"
            )}
          </span>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-lg-4">
          <input
            type="text"
            className="form-control"
            placeholder={lang("Buscar por producto...", "Search by product...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {data && (
        <div className="row">
          <div className="col-lg-12">
            <div className="material_card p-3 table-responsive">
              <table className="table table-hover vertical_middle">
                <thead className="thead-light">
                  <tr>
                    <th>{lang("Producto", "Product")}</th>
                    <th>{lang("Tamaño", "Size")}</th>
                    <th>{lang("Ingredientes", "Ingredients")}</th>
                    <th>{lang("Costo", "Cost")}</th>
                    <th>{lang("Precio", "Price")}</th>
                    <th>{lang("Margen", "Margin")}</th>
                    <th>{lang("Estado", "Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((recipe: Recipe) => {
                    const price = recipe.product?.price || 0;
                    const cost = recipe.food_cost || 0;
                    const margin = price > 0 ? price - cost : 0;
                    const marginPct =
                      price > 0 ? (margin / price) * 100 : 0;

                    return (
                      <tr
                        key={recipe.id}
                        className="cursor-pointer"
                        onClick={() =>
                          history.push(`/inventory/recipes/edit/${recipe.id}`)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="font-weight-bold">
                          {recipe.product?.title || `${lang("Producto", "Product")} #${recipe.product_id}`}
                        </td>
                        <td>
                          {recipe.size_id ? (
                            <span className="badge badge-light">
                              {lang("Tamaño", "Size")} #{recipe.size_id}
                            </span>
                          ) : (
                            <small className="text-muted">—</small>
                          )}
                        </td>
                        <td>
                          <span className="badge badge-info">
                            {recipe.ingredients?.length || 0}
                          </span>
                        </td>
                        <td>
                          {cost > 0 ? `$${cost.toFixed(2)}` : "—"}
                        </td>
                        <td>${price.toFixed(2)}</td>
                        <td className={getMarginClass(recipe)}>
                          {cost > 0
                            ? `${marginPct.toFixed(1)}%`
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`badge badge-${
                              recipe.status ? "success" : "secondary"
                            }`}
                          >
                            {recipe.status
                              ? lang("Activa", "Active")
                              : lang("Inactiva", "Inactive")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        {lang("No hay recetas registradas", "No recipes registered")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecipeList;
